import { BF_ITEMS, BF_DIMS, BF_ROLE_WEIGHTS } from "./questionBank.js";

// Hiring recommendation bands for the overall fit score.
export const BF_BANDS = [
  { min:0,  max:40,  label:"Not Recommended",         color:"#dc2626", bg:"#fef2f2", border:"#fecaca",
    desc:"The behavioural profile shows meaningful gaps against this role's requirements across multiple competencies. Proceed only with strong offsetting evidence from interview or reference checks." },
  { min:40, max:60,  label:"Proceed with Caution",     color:"#d97706", bg:"#fffbeb", border:"#fde68a",
    desc:"A mixed profile — some competencies are solid, others need probing. Use the interview to test the lower-scoring areas directly before deciding." },
  { min:60, max:80,  label:"Recommended",              color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe",
    desc:"A generally strong behavioural fit for this role. Standard interview process should confirm the profile." },
  { min:80, max:101, label:"Highly Recommended",       color:"#059669", bg:"#f0fdf4", border:"#bbf7d0",
    desc:"A strong behavioural profile across every competency weighted for this role. A high-confidence candidate for further progression." },
];

// The single source of truth for the weighted overall-fit calculation.
// Both report components import and call this directly (passing the
// candidate's already-computed dimResults and whichever role weights are
// currently selected) rather than each reimplementing this formula — a
// prior audit found all three copies agreed today, but three independent
// implementations of the same scoring math is a real drift risk the next
// time this formula needs to change.
export function computeOverallFit(dimResults, weights) {
  const overallFit = Math.round(
    Object.keys(BF_DIMS).reduce((sum, dim) => sum + (dimResults[dim]?.pct || 0) * (weights[dim] || 0), 0)
  );
  const band = BF_BANDS.find(b => overallFit >= b.min && overallFit < b.max) || BF_BANDS[0];
  return { overallFit, band };
}

export function scoreBehavioralFit(answers, roleKey = "balanced") {
  const dimScores = { LTO:0, COM:0, STP:0, ATD:0 };
  const dimCounts = { LTO:0, COM:0, STP:0, ATD:0 };

  BF_ITEMS.forEach((item, i) => {
    const raw = answers[i];
    if (raw === null || raw === undefined) return;
    dimScores[item.dim] += Number(raw);
    dimCounts[item.dim]++;
  });

  const dimResults = {};
  Object.keys(BF_DIMS).forEach(dim => {
    const max = dimCounts[dim] * 4;
    const pct = max > 0 ? Math.round((dimScores[dim] / max) * 100) : 0;
    dimResults[dim] = { raw: dimScores[dim], max, pct };
  });

  const weights = BF_ROLE_WEIGHTS[roleKey] || BF_ROLE_WEIGHTS.balanced;
  const { overallFit, band } = computeOverallFit(dimResults, weights);

  return {
    dimResults,
    overallFit,
    band,
    roleKey,
    roleLabel: weights.label,
    scales: Object.fromEntries(Object.keys(BF_DIMS).map(d => [d, dimResults[d].pct])),
    perfAvg: overallFit / 10,
    potAvg: overallFit / 10,
    perfBox: overallFit >= 60 ? 3 : overallFit >= 40 ? 2 : 1,
    potBox: overallFit >= 60 ? 3 : overallFit >= 40 ? 2 : 1,
  };
}
